import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { PageShell } from '../../../components/leaves/PageShell';
import { FileEdit } from 'lucide-react';
import CorrectionForm from './components/CorrectionForm';
import RequestHistory from './components/RequestHistory';
import ApprovalWorkflow from './components/ApprovalWorkflow';
import RequestFilters from './components/RequestFilters';
import StatusTimeline from './components/StatusTimeline';

import { Roles } from '../../../components/roles';
import {
  createCorrectionRequest,
  decideCorrectionRequest,
  getAssignedCorrectionRequests,
  getMyCorrectionRequests,
} from '../../../api/Attendance/correctionRequests';

const AttendanceCorrectionRequests = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const viewerUserId = useSelector((state) => state.user.userId);
  const viewerRole = useSelector((state) => state.user.role);
  const rtl = useSelector((state) => state.user.rtl);

  const titleIconGapClass = rtl ? 'ml-2 mr-0' : 'mr-2 ml-0';
  const headerIconGapClass = rtl ? 'ml-4 mr-0' : 'mr-4 ml-0';
  const badgeGapClass = rtl ? 'mr-2' : 'ml-2';

  const [activeTab, setActiveTab] = useState('submit'); // submit, history, approvals
  const [myRequests, setMyRequests] = useState([]);
  const [assignedRequests, setAssignedRequests] = useState([]);
  const isHr = Roles.HR.includes(viewerRole);
  const isManager = Roles.MANAGER.includes(viewerRole);
  const userRole = isHr ? 'hr' : (isManager ? 'manager' : 'employee');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: 'all',
    type: 'all'
  });

  const selectedRequestRef = useRef(null);
  useEffect(() => {
    selectedRequestRef.current = selectedRequest;
  }, [selectedRequest]);

  const prefillDate = location?.state?.prefill?.date;

  const extractApiErrorMessage = (e, fallback) => {
    if (!e) return fallback;
    if (typeof e === 'string') return e;

    const body = e?.response?.data || e?.data || e;

    if (body?.errors?.length) {
      const first = body.errors[0];
      const desc = first?.description || first?.message;
      if (desc) return desc;
    }

    const msg = body?.message || e?.message;
    if (msg) return msg;

    const status = e?.status || e?.response?.status;
    const statusText = e?.statusText || e?.response?.statusText;
    if (status) return statusText ? `${status} ${statusText}` : String(status);

    return fallback;
  };

  const resolveInternalRequestId = useCallback((requestOrId) => {
    if (!requestOrId) return null;

    if (typeof requestOrId === 'object') {
      if (typeof requestOrId?.requestId === 'number') return requestOrId.requestId;
      if (typeof requestOrId?.requestId === 'string') {
        const parsed = Number(requestOrId.requestId);
        if (Number.isFinite(parsed)) return parsed;
      }
    }

    if (typeof requestOrId === 'number') return requestOrId;

    const raw = String(requestOrId);
    const all = [...(assignedRequests || []), ...(myRequests || [])];
    const byDisplayId = all.find((r) => r?.id === raw);
    if (byDisplayId?.requestId) return byDisplayId.requestId;

    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
    return null;
  }, [assignedRequests, myRequests]);

  const tryGetInternalIdFromSelected = (requestObj) => {
    if (!requestObj || typeof requestObj !== 'object') return null;
    if (typeof requestObj?.requestId === 'number') return requestObj.requestId;
    if (typeof requestObj?.requestId === 'string') {
      const parsed = Number(requestObj.requestId);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  };

  const fetchRequests = useCallback(async () => {
    const [mineRes, assignedRes] = await Promise.all([
      getMyCorrectionRequests(viewerUserId),
      (isHr || isManager)
        ? getAssignedCorrectionRequests(viewerUserId)
        : Promise.resolve({ payload: [] }),
    ]);

    const mine = mineRes?.payload || [];
    const assigned = assignedRes?.payload || [];

    setMyRequests(mine);
    setAssignedRequests(assigned);

    return { mine, assigned };
  }, [viewerUserId, isHr, isManager]);

  const refreshRequests = useCallback(async ({ preserveSelection = true } = {}) => {
    if (!viewerUserId) return { mine: [], assigned: [] };

    const selectedInternalId = preserveSelection ? tryGetInternalIdFromSelected(selectedRequestRef.current) : null;

    setLoading(true);
    setError(null);
    try {
      const { mine, assigned } = await fetchRequests();

      if (preserveSelection && selectedInternalId) {
        const updatedSelected = [...(mine || []), ...(assigned || [])].find(
          (r) => r?.requestId === selectedInternalId
        );
        setSelectedRequest(updatedSelected || null);
      }

      return { mine, assigned };
    } catch (e) {
      setError(extractApiErrorMessage(e, t('Failed to load correction requests')));
      return { mine: [], assigned: [] };
    } finally {
      setLoading(false);
    }
  }, [viewerUserId, fetchRequests, t]);

  useEffect(() => {
    if (!viewerUserId) return;
    refreshRequests({ preserveSelection: false });
  }, [viewerUserId, isHr, isManager, refreshRequests]);

  const filteredRequests = useMemo(() => {
    // Apply filters to *my* requests
    let filtered = myRequests || [];

    if (filters?.status !== 'all') {
      filtered = filtered?.filter((req) => req?.status === filters?.status);
    }

    if (filters?.type !== 'all') {
      filtered = filtered?.filter((req) => req?.type === filters?.type);
    }

    if (filters?.dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();

      switch (filters?.dateRange) {
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          filterDate.setMonth(now.getMonth() - 3);
          break;
        default:
          break;
      }

      filtered = filtered?.filter((req) => {
        const submitted = req?.submittedDate ? new Date(req.submittedDate) : null;
        return submitted && !Number.isNaN(submitted.getTime()) ? submitted >= filterDate : true;
      });
    }

    return filtered;
  }, [filters, myRequests]);

  const handleSubmitRequest = async (requestData) => {
    try {
      setLoading(true);
      setError(null);

      const res = await createCorrectionRequest({
        date: requestData?.date,
        type: requestData?.type,
        requestedCheckIn: requestData?.requestedCheckIn,
        requestedCheckOut: requestData?.requestedCheckOut,
        reason: requestData?.reason,
        userId: viewerUserId,
      });

      // Ensure history reflects server truth (some backends don't return full payload on create)
      setFilters({ status: 'all', dateRange: 'all', type: 'all' });
      const { mine } = await refreshRequests({ preserveSelection: false });
      setActiveTab('history');

      const created = res?.payload;
      const createdInternalId = resolveInternalRequestId(created);
      if (createdInternalId) {
        const found = (mine || []).find((r) => r?.requestId === createdInternalId);
        if (found) setSelectedRequest(found);
      }
    } catch (e) {
      setError(extractApiErrorMessage(e, t('Failed to submit request')));
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestOrId, comment = '') => {
    try {
      setLoading(true);
      setError(null);

      const internalId = resolveInternalRequestId(requestOrId);
      if (!internalId) throw new Error('Invalid request');

      await decideCorrectionRequest({ requestId: internalId, isApproved: true, comment });
      await refreshRequests({ preserveSelection: true });
    } catch (e) {
      setError(extractApiErrorMessage(e, t('Failed to approve request')));
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async (requestOrId, reason = '', comment = '') => {
    try {
      setLoading(true);
      setError(null);

      const internalId = resolveInternalRequestId(requestOrId);
      if (!internalId) throw new Error(t('Invalid request'));

      const combinedComment = [reason, comment].filter(Boolean).join(' - ');
      await decideCorrectionRequest({ requestId: internalId, isApproved: false, comment: combinedComment });
      await refreshRequests({ preserveSelection: true });
    } catch (e) {
      setError(extractApiErrorMessage(e, t('Failed to reject request')));
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const getTabIcon = (tab) => {
    switch (tab) {
      case 'submit': return 'Plus';
      case 'history': return 'History';
      case 'approvals': return 'CheckSquare';
      default: return 'FileText';
    }
  };

  const getStatusStats = () => {
    const pending = filteredRequests?.filter(req => req?.status === 'pending')?.length || 0;
    const approved = filteredRequests?.filter(req => req?.status === 'approved')?.length || 0;
    const rejected = filteredRequests?.filter(req => req?.status === 'rejected')?.length || 0;
    
    return { pending, approved, rejected, total: filteredRequests?.length || 0 };
  };

  const stats = getStatusStats();

  const assignedPendingCount = useMemo(
    () => (assignedRequests || [])?.filter((req) => req?.status === 'pending')?.length || 0,
    [assignedRequests]
  );

  const tabs = [
    { id: 'submit', label: t('Submit Request'), show: true },
    { id: 'history', label: t('Request History'), show: true },
    { id: 'approvals', label: t('Pending Approvals'), show: isHr || isManager }
  ];

  return (
    <PageShell
      title={t('Attendance Correction Requests')}
      description={t('Submit and track attendance corrections with comprehensive approval workflows')}
      icon={FileEdit}
      accentColor="blue"
      maxWidth="1600px"
      stats={[
        { label: t('Total Requests'), value: stats?.total },
        { label: t('Pending'), value: stats?.pending, color: 'text-orange-600' },
        { label: t('Approved'), value: stats?.approved, color: 'text-green-600' },
        { label: t('Rejected'), value: stats?.rejected, color: 'text-red-600' },
      ]}
    >
      {/* Tab Navigation */}
      <div>
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
            {error}
          </div>
        )}

        <div className="bg-card border border-border rounded-xl">
          <div className="flex flex-wrap border-b border-border">
            {tabs?.filter(tab => tab?.show)?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center px-6 py-4 text-sm font-medium transition-colors duration-200 border-b-2 ${
                  activeTab === tab?.id
                    ? 'text-orange-600 border-orange-600 bg-orange-50/50' :'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/30'
                }`}
              >
                <Icon name={getTabIcon(tab?.id)} size={16} className={titleIconGapClass} />
                {tab?.label}
                {tab?.id === 'history' && stats?.total > 0 && (
                  <span className={`${badgeGapClass} px-2 py-0.5 text-xs bg-muted rounded-full`}>
                    {stats?.total}
                  </span>
                )}
                {tab?.id === 'approvals' && assignedPendingCount > 0 && (
                  <span className={`${badgeGapClass} px-2 py-0.5 text-xs bg-orange-100 text-orange-800 rounded-full`}>
                    {assignedPendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Tab Content */}
            {activeTab === 'submit' && (
              <div className="space-y-6">
                <CorrectionForm
                  onSubmit={handleSubmitRequest}
                  initialValues={prefillDate ? { date: prefillDate } : undefined}
                />
              </div>
            )}

            {activeTab === 'history' && (
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <RequestFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                    totalCount={filteredRequests?.length}
                  />
                </div>

                <RequestHistory
                  requests={filteredRequests}
                  onSelectRequest={setSelectedRequest}
                  selectedRequest={selectedRequest}
                  onAcceptRequest={handleAcceptRequest}
                  onRejectRequest={handleRejectRequest}
                  userRole={userRole}
                />
              </div>
            )}

            {activeTab === 'approvals' && (isHr || isManager) && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-foreground">
                    Pending Approvals
                  </h2>
                  <div className="text-sm text-muted-foreground">
                    {assignedPendingCount} requests awaiting approval
                  </div>
                </div>
                
                <ApprovalWorkflow
                  requests={assignedRequests?.filter(req => req?.status === 'pending')}
                  userRole={userRole}
                  onApproveRequest={(req, c) => handleAcceptRequest(req, c)}
                  onRejectRequest={(req, r) => handleRejectRequest(req, r, '')}
                  isProcessing={loading}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Timeline Modal/Sidebar */}
      {selectedRequest && (
        <StatusTimeline
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onAcceptRequest={handleAcceptRequest}
          onRejectRequest={handleRejectRequest}
          userRole={userRole}
        />
      )}
    </PageShell>
  );
};

export default AttendanceCorrectionRequests;